/**
 * Insights Panel Component
 * Displays analytics insights with interactive filtering and details
 */

import React, { useState } from 'react';
import { 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  ChevronRightIcon,
  FunnelIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useInsights } from '../hooks/useAnalytics';

const InsightsPanel = ({ analytics, className = '' }) => {
  const {
    selectedInsight,
    filteredInsights,
    filterByImpact,
    sortInsights,
    selectInsight,
    clearSelection,
    insightCount,
    highImpactCount,
    mediumImpactCount,
    lowImpactCount
  } = useInsights(analytics);

  const [showFilter, setShowFilter] = useState(false);
  const [sortBy, setSortBy] = useState('impact');

  const getImpactIcon = (impact) => {
    switch (impact) {
      case 'high':
        return ExclamationTriangleIcon;
      case 'medium':
        return InformationCircleIcon;
      case 'low':
        return LightBulbIcon;
      default:
        return InformationCircleIcon;
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const handleFilterChange = (impact) => {
    filterByImpact(impact);
    setShowFilter(false);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    sortInsights(newSortBy);
  };

  if (!analytics?.insights?.insights?.length) {
    return (
      <div className={`insights-panel ${className}`}>
        <div className="text-center py-8">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No insights available for this report.</p>
          <p className="text-sm text-gray-400 mt-2">
            Insights will appear here after analyzing report data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`insights-panel bg-white rounded-lg shadow-sm border ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Analytics Insights
            </h2>
            <div className="flex space-x-4 text-sm text-gray-600">
              <span className="flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                {highImpactCount} High Impact
              </span>
              <span className="flex items-center">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
                {mediumImpactCount} Medium Impact
              </span>
              <span className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                {lowImpactCount} Low Impact
              </span>
            </div>
          </div>

          {/* Filter and Sort Controls */}
          <div className="flex space-x-2">
            <div className="relative">
              <button
                onClick={() => setShowFilter(!showFilter)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filter
              </button>

              {showFilter && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={() => handleFilterChange('all')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      All Insights ({insightCount})
                    </button>
                    <button
                      onClick={() => handleFilterChange('high')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      High Impact ({highImpactCount})
                    </button>
                    <button
                      onClick={() => handleFilterChange('medium')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Medium Impact ({mediumImpactCount})
                    </button>
                    <button
                      onClick={() => handleFilterChange('low')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Low Impact ({lowImpactCount})
                    </button>
                  </div>
                </div>
              )}
            </div>

            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="impact">Sort by Impact</option>
              <option value="type">Sort by Type</option>
              <option value="title">Sort by Title</option>
            </select>
          </div>
        </div>

        {/* Confidence Score */}
        {analytics.metadata?.confidence && (
          <div className="mt-4 bg-gray-50 rounded-md p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Analysis Confidence</span>
              <span className="text-sm font-medium text-gray-900">
                {analytics.metadata.confidence.toFixed(0)}%
              </span>
            </div>
            <div className="mt-2 bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${analytics.metadata.confidence}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Insights List */}
      <div className="divide-y divide-gray-200">
        {filteredInsights.map((insight, index) => {
          const IconComponent = getImpactIcon(insight.impact);
          const colorClasses = getImpactColor(insight.impact);
          const isSelected = selectedInsight?.type === insight.type;

          return (
            <div
              key={`${insight.type}_${index}`}
              className={`p-4 cursor-pointer transition-colors ${
                isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => selectInsight(insight)}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorClasses}`}>
                  <IconComponent className="h-4 w-4" />
                </div>

                <div className="ml-3 flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {insight.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {insight.description}
                      </p>
                      
                      {/* Impact Badge */}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${colorClasses}`}>
                        {insight.impact} Impact
                      </span>
                    </div>

                    <ChevronRightIcon 
                      className={`h-5 w-5 text-gray-400 transform transition-transform ${
                        isSelected ? 'rotate-90' : ''
                      }`}
                    />
                  </div>

                  {/* Detailed View */}
                  {isSelected && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="space-y-3">
                        {/* Additional Data */}
                        {insight.data && (
                          <div className="bg-gray-50 rounded-md p-3">
                            <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                              Details
                            </h4>
                            <div className="space-y-1">
                              {Object.entries(insight.data).map(([key, value]) => (
                                <div key={key} className="flex justify-between text-sm">
                                  <span className="text-gray-600 capitalize">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}:
                                  </span>
                                  <span className="text-gray-900 font-medium">
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle action - could open detailed view or trigger action
                              console.log('View details for:', insight);
                            }}
                            className="text-xs bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors"
                          >
                            View Details
                          </button>
                          {insight.impact === 'high' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle priority action
                                console.log('Take action for:', insight);
                              }}
                              className="text-xs bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700 transition-colors"
                            >
                              Take Action
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State for Filtered Results */}
      {filteredInsights.length === 0 && analytics.insights.insights.length > 0 && (
        <div className="p-8 text-center">
          <FunnelIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No insights match the current filter.</p>
          <button
            onClick={() => handleFilterChange('all')}
            className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Click outside handler for filter dropdown */}
      {showFilter && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowFilter(false)}
        />
      )}
    </div>
  );
};

export default InsightsPanel;